import { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $menuStore, $profileFeatureStore, $profileStore, $themeStore } from '@lib/store';
import { AuthUtils, cn, PaletteUtils, RoutePaths, RouteUtils } from '@shared/utils';
import { Icon, Switcher, Text } from '@shared/widgets';
import LogoutIcon from '@assets/icons/logout.svg';
import MoonIcon from '@assets/icons/moon.svg';
import UserIcon from '@assets/icons/user.svg';

interface NavProps {
  className?: string;
}

export const Nav = (props: NavProps) => {
  const location = useLocation();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const profileStore = useUnit($profileStore.store);
  const isCollapsedMenu = useUnit($menuStore.store);
  const isDarkTheme = useUnit($themeStore.store);

  const { t } = useTranslation();

  const menu = useMemo(() => RouteUtils.getMenu(), [profileStore.data]);
  return (
    <nav className={cn('flex min-h-0 flex-1 flex-col', props.className)}>
      <ul
        className={cn(
          'flex flex-col gap-4',
          'no-scrollbar flex flex-col overflow-auto border-t border-t-gray-black py-6 dark:border-t-gray-wet-asphalt',
          PaletteUtils.isVcfcPalette && 'border-b border-b-gray-black dark:border-b-gray-wet-asphalt',
        )}
      >
        {menu.map((item) => {
          const isActive = location.pathname === item.link || location.pathname.startsWith(item.link + '/');
          return (
            <li key={item.id} className='group'>
              <NavLink
                className={cn(
                  'flex w-full cursor-pointer items-center gap-3 px-8 py-3 transition-colors duration-200',
                  PaletteUtils.isInsigniaPalette && 'hover:border-r-primary hover:bg-yellow-brown',
                  PaletteUtils.isVcfcPalette && 'hover:bg-primary-transparent hover:dark:bg-slate',
                  PaletteUtils.isPersonalPalette && 'hover:bg-primary-600',
                  isActive && [
                    'border-r-2',
                    PaletteUtils.isInsigniaPalette && 'border-r-primary bg-yellow-brown',
                    PaletteUtils.isVcfcPalette && 'border-r-white bg-primary-transparent dark:border-r-white/50 dark:bg-slate',
                    PaletteUtils.isPersonalPalette && 'bg-primary-600',
                  ],
                )}
                to={item.link}
              >
                <Icon
                  className={cn(
                    'transition-all duration-200',
                    PaletteUtils.isInsigniaPalette && ['group-hover:text-primary', isActive && 'text-primary'],
                    PaletteUtils.isVcfcPalette && ['text-white opacity-50', 'group-hover:opacity-100', isActive && 'text-white opacity-100'],
                  )}
                  icon={item.Icon}
                />
                {!isCollapsedMenu && (
                  <Text
                    className={cn(PaletteUtils.isVcfcPalette && ['group-hover:opacity-100', !isActive && 'opacity-50'])}
                    color={PaletteUtils.getPaletteParam(undefined, 'white')}
                  >
                    {t(item.name)}
                  </Text>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
      <div className={cn('mt-auto flex flex-col', PaletteUtils.isInsigniaPalette && 'border-t border-t-gray-black dark:border-t-gray-wet-asphalt')}>
        <ul className='flex flex-col py-6'>
          <li
            className={cn(
              'group flex w-full cursor-pointer items-center gap-3 px-8 py-3 transition-colors duration-200',
              PaletteUtils.isInsigniaPalette && 'hover:border-r-primary hover:bg-yellow-brown',
              PaletteUtils.isVcfcPalette && 'hover:bg-primary-transparent hover:dark:bg-slate',
              PaletteUtils.isPersonalPalette && 'hover:bg-primary-600',
            )}
            onClick={() => AuthUtils.logout()}
          >
            <Icon
              icon={
                <LogoutIcon
                  className={cn(
                    'transition-all duration-200',
                    PaletteUtils.isInsigniaPalette && 'group-hover:text-primary',
                    PaletteUtils.isVcfcPalette && ['text-white opacity-50', 'group-hover:opacity-100'],
                  )}
                />
              }
            />
            {!isCollapsedMenu && (
              <Text
                className={cn('whitespace-nowrap', PaletteUtils.isVcfcPalette && 'group-hover:opacity-100')}
                color={PaletteUtils.getPaletteParam('secondary', 'white-gray')}
              >
                {t('buttons.logout')}
              </Text>
            )}
          </li>
          <li
            className={cn(
              'group flex w-full items-center gap-3 px-8 py-3 transition-colors duration-200',
              PaletteUtils.isInsigniaPalette && 'hover:border-r-primary hover:bg-yellow-brown',
              PaletteUtils.isVcfcPalette && 'hover:bg-primary-transparent hover:dark:bg-slate',
              PaletteUtils.isPersonalPalette && 'hover:bg-primary-600',
              isCollapsedMenu && 'cursor-pointer',
            )}
            onClick={() => $themeStore.events.toggleDarkMode()}
          >
            <Icon
              icon={
                <MoonIcon
                  className={cn(
                    'transition-all duration-200',
                    PaletteUtils.isInsigniaPalette && 'group-hover:text-primary',
                    PaletteUtils.isVcfcPalette && ['text-white opacity-50', 'group-hover:opacity-100'],
                  )}
                />
              }
            />
            {!isCollapsedMenu && <Switcher label={t('sidebar.darkMode')} value={isDarkTheme} onChange={() => $themeStore.events.toggleDarkMode()} />}
          </li>
        </ul>
        <NavLink
          className={cn(
            'flex items-center gap-3 px-6 py-3 transition-colors duration-200',
            PaletteUtils.isInsigniaPalette && ['bg-primary', profileFeatureStore.data?.profileSettings?.viewProfile && 'hover:bg-primary/70'],
            PaletteUtils.isVcfcPalette && ['bg-golden-olive', profileFeatureStore.data?.profileSettings?.viewProfile && 'hover:bg-golden-olive/70'],
          )}
          to={profileFeatureStore.data?.profileSettings?.viewProfile ? RoutePaths.profile.onNavigate() : ''}
        >
          <Icon className='flex h-8 w-8 items-center justify-center rounded-full bg-very-pale-blue' icon={<UserIcon />} color='black' />
          {!isCollapsedMenu && (
            <Text weight='semibold' color='white'>
              {profileStore.data?.userName}
            </Text>
          )}
        </NavLink>
      </div>
    </nav>
  );
};
